<script lang="ts" setup>
import type { Housing } from '~/models/Housing';

const { t, locale } = useI18n();

const props = defineProps({
  housing: {
    type: Object as PropType<Housing>,
    required: true,
  },
});

const { getReadableHousingTypeById, checkPropertyOnHousingByName, getHousingsFeatureNamesByGroupName } = useHousingInfo();

interface Feature {
  key: string;
  value: string | number | boolean;
}

const features = computed(() => {
  let mainFeatures: Feature[] = [];
  let additionalFeatures: Feature[] = [];

  const flatFeatures = getHousingsFeatureNamesByGroupName(props.housing, 'flat');
  const houseFeatures = getHousingsFeatureNamesByGroupName(props.housing, 'house');
  const exteriorFeatures = getHousingsFeatureNamesByGroupName(props.housing, 'outside');
  const interiorFeatures = getHousingsFeatureNamesByGroupName(props.housing, 'inside');
  const floorFeatures = getHousingsFeatureNamesByGroupName(props.housing, 'floor');
  const heatingFeatures = getHousingsFeatureNamesByGroupName(props.housing, 'heating');
  const otherFeatures = getHousingsFeatureNamesByGroupName(props.housing, 'feature');

  switch (getReadableHousingTypeById(props.housing.type.id)) {
    case 'rent-apartment':
      mainFeatures = [
        {
          key: t('object-type'),
          value: t('offer-feature-value.apartment'),
        },
        {
          key: t('type', 1),
          value:
            flatFeatures.length > 0 ? flatFeatures.map((feature) => t(`offer-feature-value.${feature}`)).join(', ') : t('offer-feature-value.not-specified'),
        },
        getAdditionalCostsProperty(props.housing),
        getDepositProperty(props.housing),
        storeyValue(props.housing),
      ];
      additionalFeatures = [
        getPetsProperty(props.housing),
        {
          key: t('exterior-fittings'),
          value:
            exteriorFeatures.length > 0
              ? exteriorFeatures.map((feature) => t(`offer-feature-value.${feature}`)).join(', ')
              : t('offer-feature-value.not-specified'),
        },
        {
          key: t('interior-fittings'),
          value:
            interiorFeatures.length > 0
              ? interiorFeatures.map((feature) => t(`offer-feature-value.${feature}`)).join(', ')
              : t('offer-feature-value.not-specified'),
        },
        {
          key: t('other-features'),
          value:
            otherFeatures.length > 0 ? otherFeatures.map((feature) => t(`offer-feature-value.${feature}`)).join(', ') : t('offer-feature-value.not-specified'),
        },
        {
          key: t('floor'),
          value:
            floorFeatures.length > 0 ? floorFeatures.map((feature) => t(`offer-feature-value.${feature}`)).join(', ') : t('offer-feature-value.not-specified'),
        },
        {
          key: t('heating'),
          value:
            heatingFeatures.length > 0
              ? heatingFeatures.map((feature) => t(`offer-feature-value.${feature}`)).join(', ')
              : t('offer-feature-value.not-specified'),
        },
      ];
      break;
    case 'rent-shared-flat':
      mainFeatures = [
        {
          key: t('object-type'),
          value: t('offer-feature-value.shared-flat'),
        },
        {
          key: t('type', 1),
          value:
            flatFeatures.length > 0 ? flatFeatures.map((feature) => t(`offer-feature-value.${feature}`)).join(', ') : t('offer-feature-value.not-specified'),
        },
        getAdditionalCostsProperty(props.housing),
        getDepositProperty(props.housing),
        getAgeOfRoommatesProperty(props.housing),
        {
          key: t('language', 2),
          value:
            props.housing.languages && Array.isArray(props.housing.languages)
              ? props.housing.languages.map((language) => t(`offer-feature-value.language.${language}`)).join(', ')
              : t('offer-feature-value.not-specified'),
        },
        {
          key: t('shared-flat-type'),
          value: props.housing.wgType ? t(`offer-feature-value.shared-flat-type.${props.housing.wgType}`) : t('offer-feature-value.not-specified'),
        },
      ];

      additionalFeatures = [
        {
          key: t('smoker'),
          value: props.housing.smoking ? t(`offer-feature-value.smoking.${props.housing.smoking}`) : t('offer-feature-value.not-specified'),
        },
        storeyValue(props.housing),
        getPetsProperty(props.housing),
        {
          key: t('exterior-fittings'),
          value:
            exteriorFeatures.length > 0
              ? exteriorFeatures.map((feature) => t(`offer-feature-value.${feature}`)).join(', ')
              : t('offer-feature-value.not-specified'),
        },
        {
          key: t('interior-fittings'),
          value:
            interiorFeatures.length > 0
              ? interiorFeatures.map((feature) => t(`offer-feature-value.${feature}`)).join(', ')
              : t('offer-feature-value.not-specified'),
        },
        {
          key: t('other-features'),
          value:
            otherFeatures.length > 0 ? otherFeatures.map((feature) => t(`offer-feature-value.${feature}`)).join(', ') : t('offer-feature-value.not-specified'),
        },
        {
          key: t('floor'),
          value:
            floorFeatures.length > 0 ? floorFeatures.map((feature) => t(`offer-feature-value.${feature}`)).join(', ') : t('offer-feature-value.not-specified'),
        },
        {
          key: t('heating'),
          value:
            heatingFeatures.length > 0
              ? heatingFeatures.map((feature) => t(`offer-feature-value.${feature}`)).join(', ')
              : t('offer-feature-value.not-specified'),
        },
      ];
      break;
    case 'rent-house':
      mainFeatures = [
        {
          key: t('object-type'),
          value: t('offer-feature-value.house'),
        },
        {
          key: t('type', 1),
          value:
            houseFeatures.length > 0 ? houseFeatures.map((feature) => t(`offer-feature-value.${feature}`)).join(', ') : t('offer-feature-value.not-specified'),
        },
        getAdditionalCostsProperty(props.housing),
        getDepositProperty(props.housing),
        {
          key: t('house-type'),
          value: props.housing.houseType ? t(`offer-feature-value.${props.housing.houseType}`) : t('offer-feature-value.not-specified'),
        },
        {
          key: t('energy-efficiency'),
          value: props.housing.energyEfficiency ? String(props.housing.energyEfficiency).toUpperCase() : t('offer-feature-value.not-specified'),
        },
        {
          key: t('construction-year'),
          value: props.housing.constructionYear ?? t('offer-feature-value.not-specified'),
        },
      ];

      additionalFeatures = [
        storeyValue(props.housing),
        getPetsProperty(props.housing),
        {
          key: t('exterior-fittings'),
          value:
            exteriorFeatures.length > 0
              ? exteriorFeatures.map((feature) => t(`offer-feature-value.${feature}`)).join(', ')
              : t('offer-feature-value.not-specified'),
        },
        {
          key: t('interior-fittings'),
          value:
            interiorFeatures.length > 0
              ? interiorFeatures.map((feature) => t(`offer-feature-value.${feature}`)).join(', ')
              : t('offer-feature-value.not-specified'),
        },
        {
          key: t('other-features'),
          value:
            otherFeatures.length > 0 ? otherFeatures.map((feature) => t(`offer-feature-value.${feature}`)).join(', ') : t('offer-feature-value.not-specified'),
        },
        {
          key: t('floor'),
          value:
            floorFeatures.length > 0 ? floorFeatures.map((feature) => t(`offer-feature-value.${feature}`)).join(', ') : t('offer-feature-value.not-specified'),
        },
        {
          key: t('heating'),
          value:
            heatingFeatures.length > 0
              ? heatingFeatures.map((feature) => t(`offer-feature-value.${feature}`)).join(', ')
              : t('offer-feature-value.not-specified'),
        },
      ];
      break;
    default:
      break;
  }

  return [mainFeatures, additionalFeatures];
});

function getAdditionalCostsProperty(housing: Housing): Feature {
  return {
    key: t('additional-costs'),
    value: housing.sideCosts ? formatCurrency(housing.sideCosts,  locale.value, { currency: housing.currency, minimumFractionDigits: 0, maximumFractionDigits: 0 }) : t('offer-feature-value.not-specified'),
  };
}

function getDepositProperty(housing: Housing): Feature {
  return {
    key: t('deposit'),
    value: housing.deposit ? formatCurrency(housing.deposit,  locale.value, { currency: housing.currency, minimumFractionDigits: 0, maximumFractionDigits: 0 }) : t('offer-feature-value.not-specified'),
  };
}

function getAgeOfRoommatesProperty(housing: Housing): Feature {
  let value = t('offer-feature-value.not-specified');

  if (housing.flatmatesAgeMin && housing.flatmatesAgeMin > 1 && housing.flatmatesAgeMax && housing.flatmatesAgeMax < 120) {
    value = `${housing.flatmatesAgeMin} - ${housing.flatmatesAgeMax} ${t('years')}`;
  }

  return {
    key: t('age-of-roommates'),
    value,
  };
}

function getPetsProperty(housing: Housing): Feature {
  return {
    key: t('pet', 2),
    value: checkPropertyOnHousingByName(housing, 'pets-allowed') ? t('offer-feature-value.pets-allowed') : t('offer-feature-value.not-specified'),
  };
}

function storeyValue(housing: Housing): Feature {
  let storeyValue: string | number;
  switch (housing.storey) {
    case null:
      storeyValue = t('offer-feature-value.not-specified');
      break;
    case -1:
      storeyValue = t('offer-feature-value.cellar');
      break;
    case 0:
      storeyValue = t('offer-feature-value.ground-floor');
      break;
    default:
      storeyValue = housing.storey;
      break;
  }

  return {
    key: t('storey'),
    value: storeyValue,
  };
}
</script>

<template>
  <div class="features-table">
    <div v-for="featureSet in features" :key="featureSet[0].key">
      <dl v-for="feature in featureSet" :key="feature.key">
        <dt class="feature-key">{{ feature.key }}</dt>
        <dd class="feature-value">{{ feature.value }}</dd>
      </dl>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '~/assets/styles/tw-variables.scss';
@import '~/assets/styles/tw-mixins.scss';

.features-table {
  margin: 0;
  display: grid;
  grid-template-columns: 1fr;

  @include tw-is-desktop {
    grid-template-columns: 1fr 1fr;
  }

  dl {
    gap: 1rem;
    display: grid;
    padding: 0.5rem 0;
    grid-template-columns: 1fr 1fr;

    dt {
      margin: 0;
      color: $neutral-70;
    }

    dd {
      margin: 0;
    }

    border-bottom: 1px solid $lightGray;
  }

  & > div:last-of-type > dl {
    &:last-of-type {
      border-bottom: none;
    }
  }

  @include tw-is-desktop {
    dl {
      &:last-of-type {
        border-bottom: none;
      }
    }
  }
}
</style>
